import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { placeHolderData } from '../../../../config';
import {
  addTrackToPlaylist,
  checkTracksInPlaylists,
  createPlaylist,
  getPlaylists,
} from '../../../../services/api';
import {
  setPlaylistsByCategory,
  setPlaylistsLoading,
} from '../../../../store/reducers/commonStore';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { analytics } from '../../../../utils/analytics';
import { getContentType } from '../../../../utils/contentType';
import { showToast } from '../../../../utils/toast';

interface AddToPlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackId: string;
  trackName?: string; // Track name to display in modal header
  creatorName?: string; // Creator/contributor name for analytics
  category: 'music' | 'serenity' | 'book' | 'creation';
  onTrackAdded?: (playlistId: string) => void; // Callback to refresh playlist data with playlist ID
  // New props for creation support
  itemType?: 'track' | 'creation'; // Item type discriminator (defaults to 'track')
  itemId?: string; // Creation ID (required when itemType is 'creation')
  creationOrgId?: string; // Creation's org ID (for cross-org sharing)
}

interface Playlist {
  id: string;
  name: string;
  description: string;
  trackCount: number;
  thumbnailUrl?: string; // Playlist thumbnail image
  isTrackExists?: boolean; // Whether this track already exists in the playlist
}


export const AddToPlaylistModal = memo(
  ({
    isOpen,
    onClose,
    trackId,
    trackName,
    creatorName,
    category,
    onTrackAdded,
    itemType,
    itemId,
    creationOrgId,
  }: AddToPlaylistModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const reduxPlaylists = useAppSelector(
      (state) => state.commonReducer.playlistsByCategory[category]?.items || [],
    );

    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
    const [creatingPlaylist, setCreatingPlaylist] = useState(false);
    const [addingTrack, setAddingTrack] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [searchOffset, setSearchOffset] = useState(0);
    const [shouldFetch, setShouldFetch] = useState(false);
    const [createError, setCreateError] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const fetchPlaylistsRef = useRef<
      ((newOffset: number, searchValue?: string) => Promise<void>) | null
    >(null);
    const playlistNameInputRef = useRef<HTMLInputElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Focus the name input when create form opens and select all text
    useEffect(() => {
      if (showCreateForm && playlistNameInputRef.current) {
        playlistNameInputRef.current.focus();
        // Select all text so user can immediately start typing to replace it
        playlistNameInputRef.current.select();
      }
    }, [showCreateForm]);

    // Focus the search input when modal opens initially
    useEffect(() => {
      if (isOpen && !showCreateForm && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, showCreateForm]);

    // Focus the name input when there's a create error
    useEffect(() => {
      if (createError && playlistNameInputRef.current) {
        playlistNameInputRef.current.focus();
        playlistNameInputRef.current.select();
      }
    }, [createError]);

    // Track if modal was previously open to avoid resetting state on Redux updates
    const wasPreviouslyOpenRef = useRef(false);
    const lastCheckedTrackIdRef = useRef<string | null>(null);
    // Track which offsets have been checked to prevent duplicate requests
    const checkedOffsetsRef = useRef<Set<number>>(new Set());

    // Store isTrackExists info to preserve it between modal opens (for same track)
    const trackExistsMapRef = useRef<Map<string, boolean>>(new Map());
    const cachedTrackIdRef = useRef<string | null>(null);
    // Store minimum expected trackCount (playlistId -> minimum count we know exists)
    // This handles eventual consistency - if API returns stale data, we use our cached value
    const minTrackCountRef = useRef<Map<string, number>>(new Map());

    // Store reduxPlaylists in a ref to avoid triggering effect on every Redux update
    const reduxPlaylistsRef = useRef(reduxPlaylists);
    reduxPlaylistsRef.current = reduxPlaylists;

    // Reset state when modal opens and load from Redux if available
    useEffect(() => {
      if (isOpen && !wasPreviouslyOpenRef.current) {
        // Only reset on initial open, not on every Redux update
        wasPreviouslyOpenRef.current = true;
        setOffset(0);
        setSearchOffset(0);
        setHasMore(true);
        setSearchQuery('');
        // Reset the track check ref so it will check again on next open
        lastCheckedTrackIdRef.current = null;
        // Clear checked offsets when modal opens so track existence is re-checked
        checkedOffsetsRef.current.clear();

        // Clear cache if track changed
        if (cachedTrackIdRef.current !== trackId) {
          trackExistsMapRef.current.clear();
          cachedTrackIdRef.current = trackId;
        }

        // Always show cached Redux data immediately (no loading delay)
        const currentReduxPlaylists = reduxPlaylistsRef.current;
        if (currentReduxPlaylists.length > 0) {
          // Merge Redux playlists with cached isTrackExists and minimum trackCount
          const playlistsWithTrackInfo = currentReduxPlaylists.map((p) => {
            const minCount = minTrackCountRef.current.get(p.id);
            return {
              ...p,
              isTrackExists: trackExistsMapRef.current.get(p.id),
              // Use max of API value and our cached minimum (handles eventual consistency)
              trackCount:
                minCount !== undefined
                  ? Math.max(p.trackCount || 0, minCount)
                  : p.trackCount,
            };
          });
          setPlaylists(playlistsWithTrackInfo);
          setLoading(false);
          // Don't fetch again - just check track existence for current track
          setShouldFetch(false);
          // If cached playlists are less than 50, there's no more to load
          setHasMore(currentReduxPlaylists.length >= 50);
        } else {
          // No cached data, need to fetch from API
          setPlaylists([]);
          setShouldFetch(true);
        }
      } else if (!isOpen) {
        wasPreviouslyOpenRef.current = false;
        setShouldFetch(false);
        // Reset the track check ref when modal closes
        lastCheckedTrackIdRef.current = null;
        // Cleanup scroll timeout when modal closes
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      }
      // Only depend on isOpen and trackId - use ref for reduxPlaylists to avoid infinite loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, trackId]);

    // Track the previous Redux playlists length to detect deletions
    const prevReduxPlaylistsLengthRef = useRef(reduxPlaylists.length);

    // Sync local playlists with Redux ONLY when a playlist is deleted externally
    // This handles the case when user deletes a playlist from PlaylistsPage
    useEffect(() => {
      const currentReduxPlaylists = reduxPlaylistsRef.current;

      if (!isOpen || searchQuery.trim()) {
        prevReduxPlaylistsLengthRef.current = currentReduxPlaylists.length;
        return; // Don't sync during search mode
      }

      // Only sync if Redux has fewer playlists than before (deletion occurred)
      if (currentReduxPlaylists.length < prevReduxPlaylistsLengthRef.current) {
        // Get the IDs of playlists currently in Redux
        const reduxPlaylistIds = new Set(
          currentReduxPlaylists.map((p) => p.id),
        );

        // Filter out deleted playlists from local state
        setPlaylists((prev) => prev.filter((p) => reduxPlaylistIds.has(p.id)));
      }

      prevReduxPlaylistsLengthRef.current = currentReduxPlaylists.length;
      // Use reduxPlaylistsRef instead of reduxPlaylists to avoid infinite loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, searchQuery]);

    const fetchPlaylists = useCallback(
      async (newOffset: number, searchValue: string = '') => {
        if (newOffset === 0) {
          setLoading(true);
          dispatch(setPlaylistsLoading({ category, loading: true }));
        } else {
          setLoadingMore(true);
        }
        try {
          // Don't pass trackId - fetch all playlists without isTrackExists info
          const response = await getPlaylists(
            category,
            50,
            newOffset,
            searchValue || undefined,
            undefined, // Don't pass trackId to avoid refetching for each track
          );
          const items = response.items || [];

          if (newOffset === 0) {
            setPlaylists(items);
            // Save to Redux only when fetching from API (not searching)
            if (!searchValue) {
              dispatch(setPlaylistsByCategory({ category, playlists: items }));
            }
          } else {
            setPlaylists((prev) => [...prev, ...items]);
          }

          if (searchValue) {
            setSearchOffset(newOffset + 50);
          } else {
            setOffset(newOffset + 50);
          }
          // Use hasNext from API response if available, otherwise check items length
          const hasNextPage =
            response.pagination?.hasNext ?? items.length === 50;
          setHasMore(hasNextPage);
        } catch (error) {
          console.error('Error fetching playlists:', error);
          showToast.error(t('player.loadingPlaylists'));
        } finally {
          setLoading(false);
          setLoadingMore(false);
          dispatch(setPlaylistsLoading({ category, loading: false }));
        }
      },
      [category, dispatch],
    );

    // Store fetchPlaylists in ref so it can be used in other useEffects without causing re-renders
    useEffect(() => {
      fetchPlaylistsRef.current = fetchPlaylists;
    }, [fetchPlaylists]);

    // Check track existence for newly loaded playlists when offset changes
    useEffect(() => {
      if (!trackId || !isOpen || playlists.length === 0 || searchQuery.trim()) {
        return;
      }

      // Always check offset 0 first - ignore stale offset values from previous sessions
      // When modal opens, offset might not be reset to 0 yet due to async state update
      const offsetToCheck = checkedOffsetsRef.current.size === 0 ? 0 : offset;

      // Skip if we've already checked this offset
      if (checkedOffsetsRef.current.has(offsetToCheck)) {
        return;
      }

      const updateTrackExistenceForNewPlaylists = async () => {
        // Check track existence for the current page of playlists
        const trackExistsMap = await checkTracksInPlaylists(
          category,
          trackId,
          50,
          offsetToCheck,
        );

        // Mark this offset as checked
        checkedOffsetsRef.current.add(offsetToCheck);

        // Save to trackExistsMapRef for persistence between modal opens
        Object.entries(trackExistsMap).forEach(([playlistId, exists]) => {
          trackExistsMapRef.current.set(playlistId, exists);
        });

        // Update playlists with isTrackExists info from API
        // Use functional update to avoid dependency on playlists array
        setPlaylists((currentPlaylists) =>
          currentPlaylists.map((playlist) => ({
            ...playlist,
            isTrackExists:
              trackExistsMap[playlist.id] ?? playlist.isTrackExists ?? false,
          })),
        );
      };

      // Call in background without blocking UI
      updateTrackExistenceForNewPlaylists();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, trackId, category, isOpen, searchQuery]);

    // Listen for track removal events and refresh playlists from API
    useEffect(() => {
      const handleTrackRemoved = () => {
        console.log('🗑️ playlistTrackRemoved event received');
        // Refresh playlists from API when a track is removed
        if (isOpen && reduxPlaylistsRef.current.length > 0) {
          console.log('🔄 Refreshing playlists after track removal');
          fetchPlaylistsRef.current?.(0, '');
        }
      };

      window.addEventListener('playlistTrackRemoved', handleTrackRemoved);
      return () => {
        window.removeEventListener('playlistTrackRemoved', handleTrackRemoved);
      };
      // Use reduxPlaylistsRef instead of reduxPlaylists.length to avoid infinite loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Track if initial fetch has been done
    const initialFetchDoneRef = useRef(false);

    // Handle search with debounce using useEffect
    useEffect(() => {
      // Only fetch if modal is open (shouldFetch is true)
      if (!shouldFetch) {
        initialFetchDoneRef.current = false;
        return;
      }

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search API call
      searchTimeoutRef.current = setTimeout(
        () => {
          // If searching (has query text)
          if (searchQuery.trim()) {
            setPlaylists([]);
            setSearchOffset(0);
            setHasMore(true);
            fetchPlaylistsRef.current?.(0, searchQuery);
          } else {
            // If search is cleared or initial load
            setOffset(0);
            setSearchOffset(0);
            setHasMore(true);
            // Only show loading if we don't have cached playlists
            if (playlists.length === 0) {
              setLoading(true);
            }
            initialFetchDoneRef.current = true;
            fetchPlaylistsRef.current?.(0, '');
          }
        },
        searchQuery.trim() ? 300 : 0,
      ); // No debounce for initial load

      // Cleanup on unmount
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [searchQuery, shouldFetch]);

    const handleSearchChange = useCallback((value: string) => {
      setSearchQuery(value);
    }, []);

    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const offsetRef = useRef(0);
    const searchOffsetRef = useRef(0);

    // Sync offset refs with state
    useEffect(() => {
      offsetRef.current = offset;
      searchOffsetRef.current = searchOffset;
    }, [offset, searchOffset]);

    // Handle infinite scroll with debounce
    const handleScroll = useCallback(() => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;

      // If scrolled to bottom and not already loading, fetch more
      if (
        scrollHeight - scrollTop <= clientHeight + 50 &&
        !loadingMore &&
        !loading &&
        hasMore
      ) {
        // Debounce scroll to prevent multiple rapid API calls
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          const currentOffset = searchQuery
            ? searchOffsetRef.current
            : offsetRef.current;
          fetchPlaylistsRef.current?.(currentOffset, searchQuery);
        }, 200);
      }
    }, [loadingMore, loading, hasMore, searchQuery]);

    const handleCreatePlaylist = useCallback(async () => {
      if (!newPlaylistName.trim()) {
        showToast.error(t('player.nameRequired'));
        return;
      }

      setCreatingPlaylist(true);
      try {
        const newPlaylist = await createPlaylist(
          newPlaylistName,
          newPlaylistDescription,
          category,
          'private',
        );

        if (newPlaylist) {
          // Track playlist creation event
          analytics.logEvent('playlist_created', {
            playlist_id: newPlaylist.id,
            playlist_name: newPlaylist.name,
            source: `${location.pathname}${location.search || ''}`,
            content_type: getContentType(category),
          });

          // Ensure trackCount is set (default to 0 if not provided)
          const playlistWithTrackCount = {
            ...newPlaylist,
            trackCount: newPlaylist.trackCount ?? 0,
            isTrackExists: false,
          };

          setPlaylists([playlistWithTrackCount, ...playlists]);
          setNewPlaylistName('');
          setNewPlaylistDescription('');
          // Keep form visible while adding track - don't hide it yet

          // Automatically add track/creation to newly created playlist
          try {
            await addTrackToPlaylist(newPlaylist.id, trackId, {
              itemType,
              itemId,
              creationOrgId,
            });

            // Track track_added_to_playlist event
            analytics.logEvent('track_added_to_playlist', {
              playlist_id: newPlaylist.id,
              track_id: itemType === 'creation' ? itemId : trackId,
              content_name: trackName || 'Unknown',
              creator_name: creatorName || 'Unknown Artist',
              content_type: getContentType(category),
              item_type: itemType || 'track',
            });

            // Update trackExistsMapRef to mark this playlist as having the track
            trackExistsMapRef.current.set(newPlaylist.id, true);

            // Set minimum track count for this new playlist
            // New playlists start with 0, so after adding 1 track, minimum is 1
            minTrackCountRef.current.set(newPlaylist.id, 1);

            // Refetch all playlists for this category from API (without trackId)
            try {
              console.log(
                `📡 Fetching ${category} playlists from API after create...`,
              );
              const response = await getPlaylists(
                category,
                50,
                0,
                undefined,
                undefined,
              );

              if (response?.items) {
                console.log(
                  `✅ Fetched ${category} playlists (${response.items.length} items)`,
                );
                // Update Redux store with all playlists for this category
                dispatch(
                  setPlaylistsByCategory({
                    category,
                    playlists: response.items,
                  }),
                );
                console.log(
                  `📦 Redux store updated with ${category} playlists`,
                );
              }
            } catch (reduxError) {
              console.error(
                'Error refetching playlists after create:',
                reduxError,
              );
            }

            // Call the callback to refresh playlist data with the playlist ID
            onTrackAdded?.(newPlaylist.id);

            // Show success notification and close modal
            showToast.success(t('player.createdPlaylist'));
            onClose();
          } catch (error: any) {
            console.error('Error adding track to playlist:', error);
            const errorMessage =
              error?.message || t('player.addFailed');
            showToast.error(errorMessage);
          } finally {
            // Hide form and clear creating state after track addition attempt
            setShowCreateForm(false);
            setCreatingPlaylist(false);
          }
        }
      } catch (error: any) {
        console.error('Error creating playlist:', error);
        // Extract error message from API response
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create playlist';
        showToast.error(errorMessage);
        setCreatingPlaylist(false);
        // Set error flag to trigger focus on input
        setCreateError(true);
      }
    }, [
      newPlaylistName,
      newPlaylistDescription,
      category,
      playlists,
      trackId,
      onTrackAdded,
      onClose,
      dispatch,
      itemType,
      itemId,
      creationOrgId,
    ]);

    const handleAddTrackToPlaylist = useCallback(
      async (playlistId: string) => {
        setAddingTrack(playlistId);
        try {
          await addTrackToPlaylist(playlistId, trackId, {
            itemType,
            itemId,
            creationOrgId,
          });
          showToast.success(
            itemType === 'creation'
              ? t('player.creationAdded')
              : t('player.trackAdded'),
          );

          // Track track_added_to_playlist event
          analytics.logEvent('track_added_to_playlist', {
            playlist_id: playlistId,
            track_id: itemType === 'creation' ? itemId : trackId,
            content_name: trackName || 'Unknown',
            creator_name: creatorName || 'Unknown Artist',
            content_type: getContentType(category),
            item_type: itemType || 'track',
          });

          // Update trackExistsMapRef to mark this playlist as having the track
          trackExistsMapRef.current.set(playlistId, true);

          // Get current playlist to calculate new minimum track count
          const currentPlaylist = playlists.find((p) => p.id === playlistId);
          const currentCount = currentPlaylist?.trackCount || 0;
          const existingMin = minTrackCountRef.current.get(playlistId) || 0;
          // Set minimum to max of (current count + 1) or existing minimum + 1
          minTrackCountRef.current.set(
            playlistId,
            Math.max(currentCount, existingMin) + 1,
          );

          // Refetch all playlists for this category from API (without trackId)
          try {
            console.log(
              `📡 Fetching ${category} playlists from API after add...`,
            );
            const response = await getPlaylists(
              category,
              50,
              0,
              undefined,
              undefined,
            );

            if (response?.items) {
              console.log(
                `✅ Fetched ${category} playlists (${response.items.length} items)`,
              );
              // Update Redux store with all playlists for this category
              dispatch(
                setPlaylistsByCategory({
                  category,
                  playlists: response.items,
                }),
              );
              console.log(`📦 Redux store updated with ${category} playlists`);
            }
          } catch (reduxError) {
            console.error('Error refetching playlists after add:', reduxError);
          }

          // Call the callback to refresh playlist data with the playlist ID
          onTrackAdded?.(playlistId);
          onClose();
        } catch (error: any) {
          console.error('Error adding track to playlist:', error);
          // Show the specific error message from the API
          const errorMessage =
            error?.message || 'Failed to add track to playlist';
          showToast.error(errorMessage);
        } finally {
          setAddingTrack(null);
        }
      },
      [
        trackId,
        onClose,
        onTrackAdded,
        playlists,
        category,
        dispatch,
        itemType,
        itemId,
        creationOrgId,
      ],
    );

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={onClose}>
        <div
          className="bg-black/90 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white">
                {t('player.addToPlaylists')}
              </h2>
              {trackName && (
                <p className="text-sm text-white/60 mt-1 truncate">
                  {trackName}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors">
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="text-white">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden p-6 space-y-4">
            {/* Create New Playlist Section */}
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                disabled={addingTrack !== null || creatingPlaylist}
                className="w-full px-4 h-[44px] bg-white hover:bg-[#F2F2F2] disabled:opacity-50 disabled:cursor-not-allowed text-black rounded-[16px] font-medium transition-colors flex items-center justify-center flex-shrink-0">
                {t('player.createNewPlaylist')}
              </button>
            ) : (
              <div className="space-y-3 p-4 bg-white/5 rounded-lg border border-white/10 flex-shrink-0">
                <div>
                  <input
                    ref={playlistNameInputRef}
                    type="text"
                    placeholder={t('player.playlistName')}
                    value={newPlaylistName}
                    maxLength={200}
                    onChange={(e) => {
                      setNewPlaylistName(e.target.value);
                      // Clear error flag when user starts typing
                      setCreateError(false);
                    }}
                    className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
                  />
                  {/* Character counter and error message - placeholder to avoid layout jump */}
                  <div className="h-5 mt-2 px-4">
                    {newPlaylistName.length === 200 ? (
                      <p className="text-xs text-red-400 font-medium">
                        {t('player.nameLimitReached')}
                      </p>
                    ) : newPlaylistName.length >= 180 ? (
                      <span className="text-xs font-medium text-red-400">
                        {t('player.nameLimitApproaching', { count: newPlaylistName.length })}
                        /200
                      </span>
                    ) : null}
                  </div>
                </div>
                {/* <textarea
                  placeholder="Description (optional)"
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-500 resize-none h-20"
                /> */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewPlaylistName('');
                      setNewPlaylistDescription('');
                      setCreateError(false);
                    }}
                    className="flex-1 px-3 h-[44px] bg-[rgba(128,128,128,0.3)] hover:bg-[rgba(128,128,128,0.4)] border-t border-[rgba(255,255,255,0.5)] text-white rounded-[16px] font-medium transition-colors flex items-center justify-center">
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleCreatePlaylist}
                    disabled={creatingPlaylist}
                    className="flex-1 px-3 h-[44px] bg-white hover:bg-[#F2F2F2] disabled:opacity-50 text-black rounded-[16px] font-medium transition-colors flex items-center justify-center">
                    {creatingPlaylist ? t('player.creating') : t('player.create')}
                  </button>
                </div>
              </div>
            )}

            {/* Search Input */}
            <div className="relative flex-shrink-0">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('player.searchPlaylists', { category: t(`player.category${category.charAt(0).toUpperCase() + category.slice(1)}`) })}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="px-4 pr-10 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  title={t('player.clearSearch')}>
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {/* Playlists List */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto space-y-2 scrollbar-tiny">
              {loading ? (
                <div className="text-center py-8 text-white/60">
                  {t('player.loadingPlaylists')}
                </div>
              ) : playlists.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  {searchQuery
                    ? t('player.noPlaylistsFound')
                    : t('player.noPlaylistsYet')}
                </div>
              ) : (
                <>
                  {playlists.map((playlist) => (
                    <button
                      key={playlist.id}
                      onClick={() => handleAddTrackToPlaylist(playlist.id)}
                      disabled={
                        addingTrack === playlist.id || playlist.isTrackExists
                      }
                      className={`w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                        playlist.isTrackExists
                          ? 'bg-white/5 border-white/10 cursor-not-allowed opacity-60'
                          : 'bg-white/5 hover:bg-white/10 disabled:opacity-50 border-white/10'
                      }`}>
                      <div className="flex items-center justify-between gap-3">
                        {/* Thumbnail on left */}
                        <img
                          src={
                            playlist.thumbnailUrl || placeHolderData.albumCover
                          }
                          alt={playlist.name}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                        {/* Playlist info in middle */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {playlist.name}
                          </p>
                          <p className="text-white/60 text-sm">
                            {t('playlists.trackCount', { count: playlist.trackCount })}
                          </p>
                        </div>
                        {/* Status on right */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {playlist.isTrackExists && (
                            <span className="text-green-400 text-xs font-medium bg-green-500/20 px-2 py-1 rounded whitespace-nowrap">
                              {t('player.alreadyAdded')}
                            </span>
                          )}
                          {addingTrack === playlist.id && (
                            <span className="text-white text-sm whitespace-nowrap">
                              {t('player.adding')}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  {loadingMore && (
                    <div className="text-center py-4 text-white/60">
                      {t('player.loadingMorePlaylists')}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

AddToPlaylistModal.displayName = 'AddToPlaylistModal';
